import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, ChevronRight } from "lucide-react";
import { SectionHeader } from "@/components/SectionHeader";
import { api } from "@/lib/api";

/**
 * RC6 · DQ File Checklist — administrative-only record. This
 * component IS NOT a compliance declaration and IS NOT a document
 * repository. It stores boolean flags, dates, and free-text notes.
 * Access is limited to super_admin / admin (the parent gates the
 * render; the API additionally returns 403 to any other role).
 *
 * Do NOT add:
 *   - file uploads (see brief: "Do NOT accept file uploads for these
 *     items")
 *   - drug-test results, Clearinghouse query results, medical
 *     details, or any other sensitive record
 *   - an "Annual Driver Cert. of Violations" item (the 2022
 *     rulemaking eliminated that requirement, so it is intentionally
 *     omitted from every checklist section).
 */

const STATUS_STYLE = {
  current: { badge: "bg-emerald-600 text-white", label: "Current" },
  missing: { badge: "bg-critical-red text-white", label: "Missing" },
  due_soon: { badge: "bg-amber-hold text-carbon", label: "Due Soon" },
  expired: { badge: "bg-critical-red text-white", label: "Expired" },
  not_applicable: { badge: "bg-coach-silver text-carbon", label: "N/A" },
};

function StatusPill({ status, testid }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.missing;
  return (
    <span
      data-testid={testid}
      data-status={status}
      className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10.5px] uppercase tracking-widest ${s.badge}`}
    >
      {s.label}
    </span>
  );
}

function CountLine({ summary, sectionKey }) {
  const total = summary.applicable_total;
  const current = summary.current_total;
  const label = total > 0 && total === current ? "Checklist Current" : "Items Outstanding";
  return (
    <div
      className="text-[12px] text-steel"
      data-testid={`dq-summary-${sectionKey}`}
    >
      <span className="font-medium text-carbon">{current} of {total}</span>{" "}
      applicable items current · <span className="text-carbon">{label}</span>
    </div>
  );
}

function EditRow({ item, disabled, onChange }) {
  // Enforce mutual exclusivity client-side. is_complete <-> is_not_applicable
  // never both true.
  const setComplete = (v) => {
    onChange({
      is_complete: v,
      is_not_applicable: v ? false : item.is_not_applicable,
    });
  };
  const setNA = (v) => {
    onChange({
      is_not_applicable: v,
      is_complete: v ? false : item.is_complete,
    });
  };
  return (
    <tr className="border-t border-coach-silver/60" data-testid={`dq-edit-row-${item.item_key}`}>
      <td className="px-3 py-2 text-[12.5px] text-carbon align-top">
        {item.label}
      </td>
      <td className="px-3 py-2 align-top">
        <label className="inline-flex items-center gap-1 text-[11.5px]">
          <input
            type="checkbox"
            data-testid={`dq-complete-${item.item_key}`}
            checked={!!item.is_complete}
            disabled={disabled}
            onChange={(e) => setComplete(e.target.checked)}
          />
          Complete
        </label>
      </td>
      <td className="px-3 py-2 align-top">
        <label className="inline-flex items-center gap-1 text-[11.5px]">
          <input
            type="checkbox"
            data-testid={`dq-na-${item.item_key}`}
            checked={!!item.is_not_applicable}
            disabled={disabled}
            onChange={(e) => setNA(e.target.checked)}
          />
          N/A
        </label>
      </td>
      <td className="px-3 py-2 align-top">
        <input
          type="date"
          data-testid={`dq-date-verified-${item.item_key}`}
          value={item.date_verified || ""}
          disabled={disabled}
          onChange={(e) => onChange({ date_verified: e.target.value || null })}
          className="border border-coach-silver rounded-sm px-1.5 py-1 text-[11.5px] w-32"
        />
      </td>
      <td className="px-3 py-2 align-top">
        <input
          type="date"
          data-testid={`dq-expiration-${item.item_key}`}
          value={item.expiration_or_next_review_date || ""}
          disabled={disabled}
          onChange={(e) => onChange({
            expiration_or_next_review_date: e.target.value || null,
          })}
          className="border border-coach-silver rounded-sm px-1.5 py-1 text-[11.5px] w-32"
        />
      </td>
      <td className="px-3 py-2 align-top">
        <input
          type="text"
          data-testid={`dq-notes-${item.item_key}`}
          value={item.notes || ""}
          disabled={disabled}
          onChange={(e) => onChange({ notes: e.target.value })}
          className="border border-coach-silver rounded-sm px-1.5 py-1 text-[11.5px] w-full"
          maxLength={2000}
        />
      </td>
    </tr>
  );
}

function ReadRow({ item }) {
  return (
    <tr className="border-t border-coach-silver/60" data-testid={`dq-row-${item.item_key}`}>
      <td className="px-3 py-2 text-[12.5px] text-carbon align-top">
        {item.label}
      </td>
      <td className="px-3 py-2 align-top">
        <StatusPill status={item.status} testid={`dq-status-${item.item_key}`} />
      </td>
      <td className="px-3 py-2 text-[11.5px] text-steel align-top">
        {item.date_verified || <span className="text-coach-silver">—</span>}
      </td>
      <td className="px-3 py-2 text-[11.5px] text-steel align-top">
        {item.expiration_or_next_review_date || <span className="text-coach-silver">—</span>}
      </td>
      <td className="px-3 py-2 text-[11.5px] text-steel align-top whitespace-pre-wrap">
        {item.notes || <span className="text-coach-silver">—</span>}
      </td>
    </tr>
  );
}

function Section({ section, editing, draft, onDraftChange, sectionKey }) {
  const rows = editing ? draft.items : section.items;
  return (
    <div className="mb-4" data-testid={`dq-section-${sectionKey}`}>
      <div className="flex items-center justify-between">
        <SectionHeader testid={`dq-section-header-${sectionKey}`}>
          {section.label}
        </SectionHeader>
      </div>
      <CountLine summary={editing ? computeSummary(draft.items) : section.summary} sectionKey={sectionKey} />
      <div className="mt-2 overflow-x-auto">
        {!editing ? (
          <table className="w-full text-[12.5px]">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2 w-28">Status</th>
                <th className="text-left px-3 py-2 w-32">Verified</th>
                <th className="text-left px-3 py-2 w-32">Next review</th>
                <th className="text-left px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => <ReadRow key={it.item_key} item={it} />)}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2 w-24"></th>
                <th className="text-left px-3 py-2 w-24"></th>
                <th className="text-left px-3 py-2 w-36">Verified</th>
                <th className="text-left px-3 py-2 w-36">Next review</th>
                <th className="text-left px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => (
                <EditRow
                  key={it.item_key}
                  item={it}
                  disabled={false}
                  onChange={(patch) => onDraftChange(it.item_key, patch)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function computeSummary(items) {
  const applicable = items.filter((i) => !i.is_not_applicable);
  const current = applicable.filter((i) => {
    // client-side re-derivation for editing view; server remains source of truth
    const today = new Date();
    const exp = i.expiration_or_next_review_date ? new Date(i.expiration_or_next_review_date) : null;
    if (exp) {
      const diff = Math.floor((exp - today) / (1000 * 60 * 60 * 24));
      if (diff < 0) return false;
      if (diff <= 30) return false;
    }
    return !!i.is_complete;
  });
  return { applicable_total: applicable.length, current_total: current.length };
}

export function DQChecklistSection({ driverId, canEdit }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setData(null);
    api.get(`/drivers/${driverId}/dq-checklist`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  };

  useEffect(() => { load(); }, [driverId]);

  const beginEdit = () => {
    setDraft({
      core: { items: data.core.items.map((i) => ({ ...i })) },
      dat: { items: data.drug_alcohol_training.items.map((i) => ({ ...i })) },
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const setItem = (sectionKey) => (itemKey, patch) => {
    setDraft((d) => {
      const items = d[sectionKey].items.map((i) => i.item_key === itemKey ? { ...i, ...patch } : i);
      return { ...d, [sectionKey]: { ...d[sectionKey], items } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Send only the mutable fields per item.
      const collect = (rows) => rows.reduce((acc, i) => {
        acc[i.item_key] = {
          is_complete: !!i.is_complete,
          is_not_applicable: !!i.is_not_applicable,
          date_verified: i.date_verified || null,
          expiration_or_next_review_date: i.expiration_or_next_review_date || null,
          notes: i.notes || null,
        };
        return acc;
      }, {});
      const payload = { items: { ...collect(draft.core.items), ...collect(draft.dat.items) } };
      const r = await api.patch(`/drivers/${driverId}/dq-checklist`, payload);
      setData(r.data);
      setEditing(false);
      setDraft(null);
      toast.success("Checklist saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && d.code === "mutually_exclusive") {
        toast.error(d.message);
      } else {
        toast.error("Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  if (data === null) {
    return (
      <div className="bg-white border border-coach-silver rounded-md p-4 mb-4" data-testid="dq-checklist-loading">
        <SectionHeader>DQ File Checklist</SectionHeader>
        <div className="text-[12px] text-steel">Loading…</div>
      </div>
    );
  }
  if (data.error) {
    return (
      <div className="bg-white border border-coach-silver rounded-md p-4 mb-4" data-testid="dq-checklist-error">
        <SectionHeader>DQ File Checklist</SectionHeader>
        <div className="text-[12px] text-critical-red">Failed to load checklist.</div>
      </div>
    );
  }

  const draftForRender = editing
    ? {
        core: { ...data.core, items: draft.core.items },
        dat: { ...data.drug_alcohol_training, items: draft.dat.items },
      }
    : null;

  return (
    <div className="bg-white border border-coach-silver rounded-md p-4 mb-4" data-testid="dq-checklist-section">
      <div className="flex items-center justify-between">
        <SectionHeader testid="dq-checklist-header">
          <span className="inline-flex items-center gap-1.5">
            <ClipboardCheck size={13} /> DQ File Checklist
          </span>
        </SectionHeader>
        {canEdit && (
          editing ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={cancelEdit}
                data-testid="dq-cancel-btn"
                className="px-3 py-1 text-[11.5px] uppercase tracking-widest border border-coach-silver text-carbon rounded-sm"
              >Cancel</button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="dq-save-btn"
                className="px-3 py-1 text-[11.5px] uppercase tracking-widest bg-coach-blue text-white rounded-sm disabled:opacity-60"
              >{saving ? "Saving…" : "Save"}</button>
            </div>
          ) : (
            <button
              type="button"
              onClick={beginEdit}
              data-testid="dq-edit-btn"
              className="px-3 py-1 text-[11.5px] uppercase tracking-widest text-coach-blue hover:underline"
            >Edit Checklist</button>
          )
        )}
      </div>
      <div
        className="text-[11.5px] text-steel border-l-2 border-coach-silver pl-2 mb-3 mt-1"
        data-testid="dq-checklist-notice"
      >
        {data.notice}
      </div>
      <Section
        section={data.core}
        sectionKey="core"
        editing={editing}
        draft={editing ? { items: draftForRender.core.items } : null}
        onDraftChange={setItem("core")}
      />
      <Section
        section={data.drug_alcohol_training}
        sectionKey="dat"
        editing={editing}
        draft={editing ? { items: draftForRender.dat.items } : null}
        onDraftChange={setItem("dat")}
      />
    </div>
  );
}

export default DQChecklistSection;
